import { chosenOrganisationId } from "@/stores/organisation";
import { getToken } from "./session";

/**
 * Every request the shell itself makes goes through here, so the held Passport token rides along as
 * `Authorization: Bearer` (build plan §3). Hosted modules do the same with the token they read off
 * `window.PinksyGlobalState.state.token` (README "What a hosted module reads").
 */
export const AUTHORIZATION_HEADER = "Authorization";

/**
 * 🔴 **WHICH COMPANY THIS REQUEST IS ACTING FOR** (D41's layer 4) — Metaship's `apikey` header, under
 * a name that says what it is.
 *
 * It travels as a HEADER and not as a claim in the token, and the difference is the whole point:
 * identity is stable, the organisation is a choice, and the choice is whatever the human has selected
 * on screen right now. Metaship records the incident behind that rule (FIN-564): a desynced
 * credential "used to scope SILENTLY to the JWT's org — reads and writes landed in an organisation
 * the user was not looking at". A header cannot go stale while somebody keeps working.
 *
 * It is not a credential. The gate re-reads membership and standing from the database on EVERY
 * request, so a tampered value buys a refusal and nothing else.
 */
export const ORGANISATION_HEADER = "X-Organisation";

export function bearer(token: string): string {
  return `Bearer ${token}`;
}

/**
 * `init` with the session attached — the bearer token and the chosen organisation — never overriding
 * a header the caller set.
 *
 * The caller's own value wins on purpose: `GET /me/organisations` is about the PERSON and is answered
 * without one, and a module that legitimately reaches into a named organisation (an operator screen)
 * says so explicitly rather than being silently rescoped to whatever the sidebar is showing.
 */
export function withAuthorization(
  init: RequestInit = {},
  token: string | null = getToken(),
  organisationId: string | null = chosenOrganisationId(),
): RequestInit {
  if (!token && !organisationId) return init;
  const headers = new Headers(init.headers);
  if (token && !headers.has(AUTHORIZATION_HEADER)) headers.set(AUTHORIZATION_HEADER, bearer(token));
  if (organisationId && !headers.has(ORGANISATION_HEADER)) headers.set(ORGANISATION_HEADER, organisationId);
  return { ...init, headers };
}

export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/** `fetch` with the session attached. */
export function authFetch(input: RequestInfo | URL, init?: RequestInit, fetchImpl: FetchLike = (i, o) => fetch(i, o)): Promise<Response> {
  return fetchImpl(input, withAuthorization(init));
}
